import React, { useMemo, useState } from 'react';
import { ChevronDown, Clock3, PhoneMissed, Video } from 'lucide-react';
import { Message } from '../../../../services/chatService/ChatService';
import { formatSystemMessage } from '../../../../utilities/formatSystemMessage';

type ChatAppearance = 'default' | 'meeting-dark';

interface CallLogMessageProps {
    messages: Message[];
    appearance?: ChatAppearance;
}

const formatDuration = (messages: Message[]): string | null => {
    const endedMessage = [...messages]
        .reverse()
        .find((message) => message.messageContent?.toLowerCase().includes('ended'));

    if (!endedMessage?.messageContent) return null;

    const explicitDuration = endedMessage.messageContent.match(/(?:in|duration:?)[ ]+([^\-·]+?)(?:\s+-|\s+·|$)/i)?.[1]?.trim();
    if (explicitDuration) return explicitDuration;

    if (messages.length < 2) return null;
    const sorted = [...messages].sort(
        (a, b) => new Date(a.createDate).getTime() - new Date(b.createDate).getTime()
    );
    const milliseconds = new Date(sorted[sorted.length - 1].createDate).getTime() - new Date(sorted[0].createDate).getTime();
    if (!Number.isFinite(milliseconds) || milliseconds < 0) return null;

    const totalSeconds = Math.round(milliseconds / 1000);
    if (totalSeconds < 60) return `${totalSeconds}s`;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return seconds ? `${minutes}m ${seconds}s` : `${minutes} min`;
};

const CallLogMessage: React.FC<CallLogMessageProps> = ({ messages, appearance = 'default' }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const isMeetingDark = appearance === 'meeting-dark';

    const summary = useMemo(() => {
        const texts = messages.map((message) => (message.messageContent ?? '').toLowerCase());
        const declined = texts.some((text) => text.includes('rejected') || text.includes('declined'));
        const missed = texts.some((text) => text.includes('missed'));
        const ended = texts.some((text) => text.includes('ended'));
        const duration = formatDuration(messages);
        const lastMessage = [...messages].sort(
            (a, b) => new Date(b.createDate).getTime() - new Date(a.createDate).getTime()
        )[0];

        if (declined) return { label: 'Video call declined', kind: 'missed' as const, duration, lastMessage };
        if (missed) return { label: 'Missed video call', kind: 'missed' as const, duration, lastMessage };
        if (ended) return { label: 'Video call', kind: 'ended' as const, duration, lastMessage };
        return { label: 'Video call in progress', kind: 'active' as const, duration, lastMessage };
    }, [messages]);

    const timestamp = summary.lastMessage
        ? new Date(summary.lastMessage.createDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : '';

    const icon = summary.kind === 'missed'
        ? <PhoneMissed size={15} />
        : <Video size={15} />;

    const iconClass = summary.kind === 'missed'
        ? isMeetingDark ? 'text-rose-300' : 'text-rose-500'
        : isMeetingDark ? 'text-purple-300' : 'text-[#7E57C2]';

    return (
        <div className="my-2 flex w-full justify-center px-2">
            <div className={`chat-call-log w-full max-w-xl rounded-lg ${isMeetingDark ? 'bg-slate-900/80' : 'bg-slate-50/70 dark:bg-slate-800/80'}`}>
                <button
                    type="button"
                    onClick={() => setIsExpanded((current) => !current)}
                    className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left transition-colors ${isMeetingDark ? 'hover:bg-slate-800/70' : 'hover:bg-slate-100 dark:hover:bg-slate-700/80'}`}
                    aria-expanded={isExpanded}
                >
                    <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${isMeetingDark ? 'bg-slate-800' : 'bg-white dark:bg-slate-700'} ${iconClass}`}>
                        {icon}
                    </span>

                    <span className="min-w-0 flex-1">
                        <span className={`flex min-w-0 items-center gap-1.5 text-[12px] font-semibold ${isMeetingDark ? 'text-slate-200' : 'text-slate-700 dark:text-slate-100'}`}>
                            <span className="truncate">{summary.label}</span>
                            {summary.duration && (
                                <>
                                    <span className={isMeetingDark ? 'text-slate-600' : 'text-slate-300 dark:text-slate-600'}>·</span>
                                    <span className={`inline-flex shrink-0 items-center gap-1 font-medium ${isMeetingDark ? 'text-slate-400' : 'text-slate-500 dark:text-slate-400'}`}>
                                        <Clock3 size={11.5} />
                                        {summary.duration}
                                    </span>
                                </>
                            )}
                        </span>
                    </span>

                    <span className={`shrink-0 text-[10.5px] ${isMeetingDark ? 'text-slate-500' : 'text-slate-400 dark:text-slate-500'}`}>{timestamp}</span>
                    <ChevronDown
                        size={14}
                        className={`shrink-0 transition-transform ${isMeetingDark ? 'text-slate-500' : 'text-slate-400 dark:text-slate-500'} ${isExpanded ? 'rotate-180' : ''}`}
                    />
                </button>

                {isExpanded && (
                    <div className={`border-t-[1px] px-3 py-2 ${isMeetingDark ? 'border-slate-800' : 'border-slate-200 dark:border-slate-700'}`}>
                        <div className="space-y-1.5">
                            {[...messages]
                                .sort((a, b) => new Date(a.createDate).getTime() - new Date(b.createDate).getTime())
                                .map((message) => (
                                    <div key={message.id} className="flex items-start justify-between gap-4 text-[11px]">
                                        <span className={`min-w-0 flex-1 truncate ${isMeetingDark ? 'text-slate-400' : 'text-slate-600 dark:text-slate-300'}`}>
                                            {formatSystemMessage(message.messageContent).split('·')[0].trim()}
                                        </span>
                                        <span className={`shrink-0 text-[10px] ${isMeetingDark ? 'text-slate-600' : 'text-slate-400 dark:text-slate-500'}`}>
                                            {new Date(message.createDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                    </div>
                                ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CallLogMessage;
